import { useCallback, useEffect, useMemo, useState } from 'react';
import { ANEP_CLIENT_ID, ConfigTemplateCreate, DeviceType, JsonConfig, isAnepTemplate } from '../../../types/configTemplate';
import { apiClient } from '../../../services/apiClient';
import { clientService } from '../../../services/clientService';
import { useI18n } from '../../../contexts/I18nContext';
import { findExistingDefault, ExistingDefault } from '../defaultConflict';
import { FieldErrors, FieldValues, fieldValuesToJsonConfig, schemaDefaults, validateFieldFormats, validateTemplateName } from '../templateSchema';
import { useTemplateSchema } from '../useTemplateSchema';

interface DefaultConflict {
  existing: ExistingDefault;
  next: (withDefault: boolean) => void | Promise<void>;
}

/**
 * The new-template form: owner, gateway type, name, the values from the
 * gateway schema, and the save with its "replace the default?" guard and
 * its impact preview.
 */
export const useCreateTemplate = (isOpen: boolean, onClose: () => void, onTemplateCreated: (template: ConfigTemplateCreate) => void) => {
  const { t } = useI18n();
  const axios = useMemo(() => apiClient.getAxiosInstance(), []);

  // Identity
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [deviceType, setDeviceType] = useState<DeviceType>('');
  const [ownerId, setOwnerId] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  // Values: the schema follows the gateway type; its defaults prefill the empty fields.
  const [fieldValues, setFieldValues] = useState<FieldValues>({});
  const { schema, schemaLoading, groupedSchema, passwordInSchema } = useTemplateSchema(deviceType, true, (fields) => {
    setFieldValues((prev) => ({ ...schemaDefaults(fields), ...prev }));
  });
  const [password, setPassword] = useState('');
  const [generatePassword, setGeneratePassword] = useState(false);

  // Dialog
  const [errors, setErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [defaultConflict, setDefaultConflict] = useState<DefaultConflict | null>(null);

  // No type, no values.
  useEffect(() => {
    if (!deviceType) setFieldValues({});
  }, [deviceType]);

  // The owner's name, for the footer summary.
  useEffect(() => {
    if (!ownerId) { setOwnerName(''); return; }
    if (ownerId === ANEP_CLIENT_ID) { setOwnerName(t('configTemplates.anep')); return; }
    let cancelled = false;
    clientService.fetchActiveClients()
      .then((list) => { if (!cancelled) setOwnerName(list.find((c) => c.id === ownerId)?.name || ''); })
      .catch(() => { if (!cancelled) setOwnerName(''); });
    return () => { cancelled = true; };
  }, [ownerId, t]);

  const setFieldValue = useCallback((fieldName: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [fieldName]: value }));
    setErrors((prev) => (prev[fieldName] ? { ...prev, [fieldName]: undefined } : prev));
  }, []);

  const clearError = (key: string) => setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));

  /** How many values are set so far, for the footer summary. */
  const setCount = useMemo(
    () => Object.values(fieldValues).filter((v) => v !== undefined && v !== '').length + (passwordInSchema && (generatePassword || password.trim()) ? 1 : 0),
    [fieldValues, passwordInSchema, generatePassword, password],
  );
  const summaryReady = Boolean(name.trim() && ownerId && deviceType);

  const validate = (): boolean => {
    const next: FieldErrors = { ...validateFieldFormats(schema, fieldValues, t) };
    const nameError = validateTemplateName(name, t);
    if (nameError) next.name = nameError;
    if (!deviceType) next.device_type = t('configTemplates.validation.deviceTypeRequired');
    if (!ownerId) next.client_id = t('configTemplates.ownerRequired');
    setErrors(next);
    return Object.values(next).every((v) => !v);
  };

  /** The POST body; `withDefault` overrides the switch for the conflict dialog. */
  const buildPayload = useCallback((withDefault?: boolean): ConfigTemplateCreate => {
    const jsonConfig: JsonConfig = fieldValuesToJsonConfig(schema, fieldValues);
    if (passwordInSchema) {
      jsonConfig.generate_password = generatePassword;
      if (!generatePassword && password.trim()) jsonConfig.password = password.trim();
    }
    return {
      name: name.trim(),
      description: description.trim() || undefined,
      client_id: ownerId,
      device_type: deviceType,
      is_default: withDefault ?? isDefault,
      json_config: jsonConfig,
    };
  }, [schema, fieldValues, passwordInSchema, generatePassword, password, name, description, ownerId, deviceType, isDefault]);

  const reset = () => {
    setDefaultConflict(null);
    setName('');
    setDescription('');
    setOwnerId('');
    setIsDefault(false);
    setDeviceType('');
    setPassword('');
    setGeneratePassword(false);
    setFieldValues({});
    setErrors({});
    setError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const persist = async (withDefault?: boolean) => {
    const payload = buildPayload(withDefault);
    await axios.post('/v2/config-templates', payload);
    onTemplateCreated(payload);
    reset();
    onClose();
  };

  const runSave = async (withDefault: boolean) => {
    setIsLoading(true);
    try {
      await persist(withDefault);
    } catch (err: any) {
      console.error('[CREATE CONFIG TEMPLATE] Error:', err);
      setError(err.response?.data?.detail || err.message || t('configTemplates.create.failed'));
    } finally {
      setIsLoading(false);
    }
  };

  /** Asks before taking the default flag from an existing template of the same owner and gateway. */
  const guardDefault = async (next: (withDefault: boolean) => void | Promise<void>) => {
    if (isDefault) {
      const existing = await findExistingDefault(axios, ownerId, deviceType, null);
      if (existing) {
        setDefaultConflict({ existing, next });
        return;
      }
    }
    await next(isDefault);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    await guardDefault(runSave);
  };

  const openPreview = async () => {
    setError(null);
    if (!validate()) return;
    await guardDefault((withDefault) => {
      if (!withDefault) setIsDefault(false);
      setShowPreview(true);
    });
  };

  const confirmFromPreview = async () => {
    setError(null);
    setIsLoading(true);
    try {
      await persist();
      setShowPreview(false);
    } catch (err: any) {
      console.error('[CREATE CONFIG TEMPLATE] Error:', err);
      setError(err.response?.data?.detail || err.message || t('configTemplates.create.failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const resolveConflict = (withDefault: boolean | null) => {
    const pending = defaultConflict;
    setDefaultConflict(null);
    if (!pending || withDefault === null) return;
    if (!withDefault) setIsDefault(false);
    void pending.next(withDefault);
  };

  const previewRequest = showPreview
    ? {
        template_id: null,
        name: name.trim() || undefined,
        device_type: deviceType,
        json_config: buildPayload().json_config as Record<string, unknown>,
        client_ids: ownerId && !isAnepTemplate(ownerId) ? [ownerId] : null,
      }
    : null;

  return {
    name, setName, description, setDescription, deviceType, setDeviceType, ownerId, setOwnerId, ownerName, isDefault, setIsDefault,
    schema, schemaLoading, groupedSchema, passwordInSchema, fieldValues, setFieldValue, password, setPassword, generatePassword, setGeneratePassword,
    errors, clearError, error, isLoading, setCount, summaryReady,
    submit, openPreview, confirmFromPreview, close, showPreview, setShowPreview, previewRequest, defaultConflict, resolveConflict,
  };
};

export type CreateTemplateState = ReturnType<typeof useCreateTemplate>;
